import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { API_DOMAIN } from "config";
import PaymentMethodDisplayCard from "components/PaymentMethodDisplayCard";
import DealerCardSetupModal from "components/DealerCardSetupModal";
import useDealerPaymentMethods from "hooks/useDealerPaymentMethods";
import styles from "./OfferPaymentConfirmModal.module.css";

const CONFIRMATION_TEXT_VERSION = "v1";

const REGULAR_TERMS_TEXT =
  "לתשומת לב, מתן הצעה הינו מחייב. במידה והספק יאשר את ההצעה שלך, יהיה עליך לסגור את העסקה לפי הסכום שהצעת. אי סגירת העסקה יחייב את כרטיס האשראי בסכום של 2% מסך העסקה.";

const RESERVATION_TERMS_TEXT =
  'שיריון הרכב הינו בתפיסת מסגרת בכרטיס האשראי בסך 2,000 ש"ח שיוחזרו לאחר סיום העסקה במידה ולא זכית. במידה והספק אישר את העסקה וזכית, סכום השיריון לא יוחזר וייחשב כמקדמה מתוך כל התשלום עבור הרכב.';

const AUCTION_TERMS_TEXT =
  'הגשת הצעה במכרז מחייבת תפיסת מסגרת בכרטיס האשראי בסך 2,000 ש"ח שיוחזרו לאחר סיום המכרז במידה ולא זכית. במידה וזכית במכרז, סכום התפיסה לא יוחזר וייחשב כמקדמה מתוך כל התשלום עבור הרכב.';

const IMMEDIATE_PURCHASE_TERMS_TEXT =
  "רכישה מידית תחייב את כרטיס האשראי ב־5% מהמחיר המומלץ של הרכב.";

export default function OfferPaymentConfirmModal({
  open,
  userId,
  car,
  offerAmount,
  recommendedPrice,
  minimumOfferPrice,
  wantsReservation,
  mode = "regular",
  onClose,
  onSubmitConfirmed,
  offerId = null,
  existingReservationHoldId = null,
  existingPaymentMethodId = null,
}) {
  const { methods, defaultMethodId, loading: methodsLoading, fetchMethods, addMethod } =
    useDealerPaymentMethods();
  const [selectedMethodId, setSelectedMethodId] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showCardSetup, setShowCardSetup] = useState(false);
  const [isCreatingHold, setIsCreatingHold] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [holdError, setHoldError] = useState("");
  const [validationError, setValidationError] = useState("");
  const [reservationHoldEnabled, setReservationHoldEnabled] = useState(null);
  const submittingRef = useRef(false);
  const createdHoldRef = useRef(null);

  const resetHoldRef = React.useCallback(() => {
    if (existingReservationHoldId) {
      createdHoldRef.current = {
        hold_id: existingReservationHoldId,
        payment_method_id: existingPaymentMethodId ? Number(existingPaymentMethodId) : null,
        offer_id: offerId ? Number(offerId) : null,
      };
    } else {
      createdHoldRef.current = null;
    }
  }, [existingReservationHoldId, existingPaymentMethodId, offerId]);

  useEffect(() => {
    if (!open) {
      setTermsAccepted(false);
      setHoldError("");
      setValidationError("");
      setIsCreatingHold(false);
      setIsSubmitting(false);
      setReservationHoldEnabled(null);
      submittingRef.current = false;
      resetHoldRef();
      return;
    }
    setTermsAccepted(false);
    setHoldError("");
    setValidationError("");
    setReservationHoldEnabled(null);
    resetHoldRef();

    axios
      .get(`${API_DOMAIN}/billing/reservationHoldEnabled`)
      .then((res) => setReservationHoldEnabled(res?.data?.enabled === true))
      .catch(() => setReservationHoldEnabled(false));

    fetchMethods(userId).then((data) => {
      const paymentMethods = data?.payment_methods || [];
      const defId = existingPaymentMethodId || data?.default_payment_method_id || paymentMethods[0]?.id;
      setSelectedMethodId(defId ? Number(defId) : null);
    });
  }, [open, userId, fetchMethods, existingReservationHoldId, existingPaymentMethodId, offerId, resetHoldRef]);

  useEffect(() => {
    if (!open) return;
    if (methods.length === 1 && !selectedMethodId) {
      setSelectedMethodId(Number(methods[0].id));
    }
  }, [methods, open, selectedMethodId]);

  if (!open) return null;

  const portalTarget = typeof document !== "undefined" ? document.body : null;
  if (!portalTarget) return null;

  const immediatePurchaseActive = mode === "immediatePurchase";
  const reservationActive = !immediatePurchaseActive && !!wantsReservation && reservationHoldEnabled === true;
  const reservationDisabledAfterRequest = !immediatePurchaseActive && !!wantsReservation && reservationHoldEnabled === false;
  const auctionActive = Number(car?.is_auction ?? car?.isAuction ?? 0) === 1 || car?.is_auction === true;
  const reservationTermsText = auctionActive ? AUCTION_TERMS_TEXT : RESERVATION_TERMS_TEXT;

  const carName = [car?.manufacturer, car?.model, car?.year].filter(Boolean).join(" ");
  const isLoading = isCreatingHold || isSubmitting || methodsLoading || (!immediatePurchaseActive && !!wantsReservation && reservationHoldEnabled === null);

  const immediatePurchaseAmount =
    Number(recommendedPrice) > 0 ? Math.ceil(Number(recommendedPrice) * 0.05) : 0;
  const offerStartingPrice = Number(minimumOfferPrice ?? recommendedPrice ?? 0);

  const termsText = immediatePurchaseActive
    ? IMMEDIATE_PURCHASE_TERMS_TEXT
    : reservationActive
    ? reservationTermsText
    : REGULAR_TERMS_TEXT;

  const checkboxText = immediatePurchaseActive
    ? "אני מאשר/ת חיוב של 5% מהמחיר המומלץ של הרכב לצורך רכישה מידית."
    : reservationActive
    ? auctionActive
      ? "אני מאשר/ת את תנאי הגשת ההצעה במכרז"
      : "אני מאשר/ת את תנאי שיריון הרכב"
    : "אני מאשר/ת את תנאי הגשת ההצעה";

  const handleCardSaved = (paymentMethod) => {
    addMethod(paymentMethod);
    fetchMethods(userId).then((data) => {
      setSelectedMethodId(Number(paymentMethod?.id) || data?.default_payment_method_id || null);
    });
    setShowCardSetup(false);
  };

  const handleSubmit = async () => {
    if (submittingRef.current || isLoading) return;
    if (!selectedMethodId) {
      setValidationError("יש לבחור אמצעי תשלום להמשך");
      return;
    }
    if (!termsAccepted) {
      setValidationError(
        immediatePurchaseActive
          ? "יש לאשר את חיוב 5% מהמחיר המומלץ לצורך רכישה מידית."
          : "יש לאשר את תנאי הגשת ההצעה לפני ההמשך."
      );
      return;
    }

    submittingRef.current = true;
    setHoldError("");
    setValidationError("");

    try {
      if (immediatePurchaseActive) {
        setIsSubmitting(true);
        try {
          const res = await axios.post(`${API_DOMAIN}/billing/chargeImmediatePurchase`, {
            user_id: userId,
            car_id: Number(car?.car_id ?? car?.id),
            offer_id: offerId ? Number(offerId) : null,
            payment_method_id: selectedMethodId,
            immediate_purchase_terms_accepted: 1,
            confirmation_accepted: 1,
            confirmation_text: IMMEDIATE_PURCHASE_TERMS_TEXT,
            confirmation_accepted_at: new Date().toISOString(),
          });

          if (!res?.data?.success) {
            throw new Error(res?.data?.message || res?.data?.error || "immediate_purchase_failed");
          }

          await onSubmitConfirmed({
            payment_method_id: selectedMethodId,
            is_reserved: 0,
            reserve_requested: 0,
            reservation_hold_id: null,
            immediate_purchase: 1,
            immediate_purchase_transaction_id: res.data.transaction_id,
            immediate_purchase_terms_accepted: 1,
            confirmation_accepted: 1,
            confirmation_text_version: CONFIRMATION_TEXT_VERSION,
            confirmation_text: IMMEDIATE_PURCHASE_TERMS_TEXT,
            confirmation_accepted_at: new Date().toISOString(),
            penalty_percent: 0,
          });
        } catch (err) {
          setHoldError(
            err?.response?.data?.error ||
              err?.response?.data?.message ||
              err?.message ||
              "חיוב הרכישה המידית נכשל. ההצעה לא נשמרה."
          );
        } finally {
          setIsSubmitting(false);
        }
      } else if (reservationActive) {
        setIsCreatingHold(true);
        let holdData = createdHoldRef.current;
        try {
          const selectedMatchesExistingHold =
            existingReservationHoldId &&
            existingPaymentMethodId &&
            Number(existingPaymentMethodId) === Number(selectedMethodId);
          const selectedMatchesCachedHold =
            holdData?.hold_id &&
            holdData?.payment_method_id &&
            Number(holdData.payment_method_id) === Number(selectedMethodId);

          if (selectedMatchesExistingHold) {
            console.log("Using existing hold ID from offer:", existingReservationHoldId);
            holdData = {
              hold_id: existingReservationHoldId,
              payment_method_id: Number(existingPaymentMethodId),
              offer_id: offerId ? Number(offerId) : null,
            };
            setIsCreatingHold(false);
          } else if (!selectedMatchesCachedHold) {
            console.log("Creating new hold");
            const res = await axios.post(`${API_DOMAIN}/billing/createReservationHold`, {
              user_id: userId,
              car_id: Number(car?.car_id ?? car?.id),
              offer_id: offerId ? Number(offerId) : null,
              offer_amount: Number(offerAmount),
              payment_method_id: selectedMethodId,
              replace_existing_hold: existingReservationHoldId && !selectedMatchesExistingHold ? 1 : 0,
              reservation_terms_accepted: 1,
              confirmation_accepted: 1,
              confirmation_text: reservationTermsText,
              confirmation_accepted_at: new Date().toISOString(),
            });
            if (!res?.data?.success) throw new Error(res?.data?.message || "hold_failed");
            holdData = {
              ...res.data.data,
              payment_method_id: Number(selectedMethodId),
              offer_id: offerId ? Number(offerId) : null,
            };
            createdHoldRef.current = holdData;
            setIsCreatingHold(false);
          } else {
            console.log("Using cached hold from ref:", holdData);
            setIsCreatingHold(false);
          }
        } catch (err) {
          setHoldError(
            err?.response?.data?.message ||
              "לא הצלחנו לבצע תפיסת מסגרת לשיריון הרכב. ניתן לנסות שוב או להגיש הצעה ללא שיריון."
          );
          return;
        } finally {
          setIsCreatingHold(false);
        }

        setIsSubmitting(true);
        try {
          await onSubmitConfirmed({
            payment_method_id: selectedMethodId,
            reserve_requested: 1,
            reservation_hold_id: holdData.hold_id,
            reservation_terms_accepted: 1,
            confirmation_accepted: 1,
            confirmation_text_version: CONFIRMATION_TEXT_VERSION,
            confirmation_text: reservationTermsText,
            confirmation_accepted_at: new Date().toISOString(),
          });
        } catch (err) {
          setHoldError(
            err?.response?.data?.error ||
              "תפיסת המסגרת בוצעה אך שמירת ההצעה נכשלה. המסגרת תשוחרר אוטומטית. נסה שוב."
          );
          createdHoldRef.current = null;
        } finally {
          setIsSubmitting(false);
        }
      } else {
        setIsSubmitting(true);
        try {
          await onSubmitConfirmed({
            payment_method_id: selectedMethodId,
            reserve_requested: 0,
            reservation_hold_id: null,
            regular_terms_accepted: 1,
            confirmation_accepted: 1,
            confirmation_text_version: CONFIRMATION_TEXT_VERSION,
            confirmation_text: REGULAR_TERMS_TEXT,
            confirmation_accepted_at: new Date().toISOString(),
            penalty_percent: 2,
          });
        } catch (err) {
          setHoldError(err?.response?.data?.error || "שמירת ההצעה נכשלה. נסה שוב.");
        } finally {
          setIsSubmitting(false);
        }
      }
    } finally {
      submittingRef.current = false;
    }
  };

  const loadingLabel = isCreatingHold
    ? "מבצעים תפיסת מסגרת..."
    : isSubmitting && immediatePurchaseActive
    ? "מבצעים חיוב רכישה מידית..."
    : isSubmitting
    ? "מגישים את ההצעה..."
    : methodsLoading
    ? "בודקים אמצעי תשלום..."
    : null;

  const submitLabel = immediatePurchaseActive
    ? "אישור חיוב רכישה מידית והגשת הצעה"
    : reservationActive
    ? "אישור, תפיסת מסגרת והגשת הצעה"
    : "אישור והגשת הצעה";

  return createPortal(
    <>
      {showCardSetup && (
        <DealerCardSetupModal
          open={showCardSetup}
          userId={userId}
          mode="offer"
          onClose={() => {
            setShowCardSetup(false);
            if (methods.length === 0) onClose();
          }}
          onCompleted={handleCardSaved}
          onContinue={() => setShowCardSetup(false)}
        />
      )}

      {!showCardSetup && (
        <div className={styles.overlay} dir="rtl" role="dialog" aria-modal="true" aria-labelledby="offer-confirm-title">
          <div className={styles.modal}>
            <button type="button" className={styles.closeButton} onClick={onClose} aria-label="סגור" disabled={isLoading}>
              ✕
            </button>

            <header className={styles.header}>
              <h2 id="offer-confirm-title">{immediatePurchaseActive ? "אישור רכישה מידית" : "אישור הגשת הצעה"}</h2>
              <p>בדוק את פרטי ההצעה ואמצעי התשלום לפני המשך.</p>
            </header>

            <div className={styles.section}>
              <p className={styles.sectionTitle}>סיכום ההצעה</p>
              {carName && (
                <div className={styles.summaryRow}>
                  <span className={styles.summaryLabel}>רכב</span>
                  <span className={styles.summaryValue}>{carName}</span>
                </div>
              )}
              {offerStartingPrice > 0 && (
                <div className={styles.summaryRow}>
                  <span className={styles.summaryLabel}>{auctionActive ? "מחיר להצעה החל מ" : "מחיר מומלץ"}</span>
                  <span className={styles.summaryValue}>₪{Number(offerStartingPrice).toLocaleString("he-IL")}</span>
                </div>
              )}
              {!immediatePurchaseActive && (
                <div className={styles.summaryRow}>
                  <span className={styles.summaryLabel}>סכום ההצעה שלך</span>
                  <span className={styles.summaryValueRed}>₪{Number(offerAmount).toLocaleString("he-IL")}</span>
                </div>
              )}
              {immediatePurchaseActive && (
                <div className={styles.summaryRow}>
                  <span className={styles.summaryLabel}>חיוב רכישה מידית</span>
                  <span className={styles.summaryValueRed}>
                    ₪{Number(immediatePurchaseAmount).toLocaleString("he-IL")}
                  </span>
                </div>
              )}
            </div>

            <div className={styles.section}>
              <p className={styles.sectionTitle}>אמצעי תשלום</p>
              {methodsLoading ? (
                <div className={styles.loadingRow}>
                  <span className={styles.spinner} />
                  <span>בודקים אמצעי תשלום...</span>
                </div>
              ) : methods.length === 0 ? (
                <button type="button" className={styles.secondaryButton} onClick={() => setShowCardSetup(true)} disabled={isLoading}>
                  הוספת כרטיס אשראי
                </button>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {methods.map((m) => (
                    <PaymentMethodDisplayCard
                      key={m.id}
                      paymentMethod={m}
                      variant="compact"
                      selectable
                      selected={Number(m.id) === Number(selectedMethodId)}
                      isDefault={Number(m.id) === Number(defaultMethodId)}
                      onSelect={(pm) => setSelectedMethodId(Number(pm.id))}
                    />
                  ))}
                  <button
                    type="button"
                    className={styles.ghostButton}
                    style={{ fontSize: 13, marginTop: 4 }}
                    onClick={() => setShowCardSetup(true)}
                    disabled={isLoading}
                  >
                    + הוספת כרטיס נוסף
                  </button>
                </div>
              )}
              {!selectedMethodId && methods.length > 0 && (
                <p style={{ color: "#c5221f", fontSize: 13, marginTop: 8 }}>יש לבחור אמצעי תשלום להמשך</p>
              )}
            </div>

            {reservationDisabledAfterRequest && (
              <div className={styles.errorMsg}>
                אפשרות השיריון אינה פעילה כרגע. ניתן להגיש הצעה רגילה ללא שיריון.
              </div>
            )}

            <div className={`${styles.termsBox} ${reservationActive || immediatePurchaseActive ? styles.termsBoxReservation : ""}`}>
              <p style={{ margin: 0 }}>{termsText}</p>
              <label className={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => {
                    setTermsAccepted(e.target.checked);
                    if (e.target.checked) setValidationError("");
                  }}
                  disabled={isLoading}
                />
                <span>{checkboxText}</span>
              </label>
            </div>

            {validationError && <div className={styles.errorMsg}>{validationError}</div>}
            {holdError && <div className={styles.errorMsg}>{holdError}</div>}

            {loadingLabel && (
              <div className={styles.loadingRow}>
                <span className={styles.spinner} />
                <span>{loadingLabel}</span>
              </div>
            )}

            <div className={styles.actions}>
              <button type="button" className={styles.primaryButton} onClick={handleSubmit} disabled={isLoading}>
                {submitLabel}
              </button>
              <button type="button" className={styles.ghostButton} onClick={onClose} disabled={isLoading}>
                ביטול
              </button>
            </div>
          </div>
        </div>
      )}
    </>,
    portalTarget
  );
}
